import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  setGameResult,
  setTicTacToeModalOpen,
  setConnectFourModalOpen,
  setBattleshipModalOpen,
  setOpponent,
  setGameStatus,
} from '../redux/slices/gameParrotSlice';
import { fetchQuestions, fetchTags } from '../redux/slices/questionsSlice';
import {
  currentUserSelector,
  ulistSelector,
  fetchUsers,
  fetchProfileUser,
  authStatusSelector,
} from '../redux/slices/userSlice';
import { Question, Answer, User, ByteBuddiesSocket } from '../types';

const useByteBuddies = (socket: ByteBuddiesSocket | null) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector(currentUserSelector);
  const users = useAppSelector(ulistSelector);

  // Fetches the current user for the profile page every time it changes
  useEffect(() => {
    if (user && location.pathname.includes(user?.username))
      dispatch(fetchProfileUser({ identifier: user?._id || '', idType: '_id' }));
  }, [dispatch, location, user]);

  // Socket updates
  useEffect(() => {
    const handleUserUpdate = (uid: string) => {
      dispatch(fetchUsers());
      dispatch(fetchProfileUser({ identifier: uid, idType: '_id' }));
    };

    const handleQuestionUpdate = (question: Question) => {
      dispatch(fetchUsers());
      dispatch(fetchProfileUser({ identifier: question.askedByUser._id || '', idType: '_id' }));
    };

    const handleAnswerUpdate = ({ qid: id, answer }: { qid: string; answer: Answer }) => {
      dispatch(fetchUsers());
      dispatch(fetchProfileUser({ identifier: answer.ansByUser._id || '', idType: '_id' }));
    };

    const handleCommentUpdate = ({
      result,
      type,
    }: {
      result: Question | Answer;
      type: 'question' | 'answer';
    }) => {
      dispatch(fetchUsers());
      if (type === 'question')
        dispatch(
          fetchProfileUser({
            identifier: (result as Question).askedByUser._id || '',
            idType: '_id',
          }),
        );
      if (type === 'answer')
        dispatch(
          fetchProfileUser({
            identifier: (result as Answer).ansByUser._id || '',
            idType: '_id',
          }),
        );
    };

    const handleGameUpdate = ({
      waiting,
      result,
    }: {
      waiting: User | undefined;
      result: (string | number[])[];
    }) => {
      if (result[0] === 'quit') dispatch(setGameResult(result));
      if (waiting?.game.length && user?._id === waiting._id) {
        if (waiting.game.length === 1) {
          if (waiting.game[0][2] === 'tictactoe') dispatch(setTicTacToeModalOpen(true));
          if (waiting.game[0][2] === 'connectfour') dispatch(setConnectFourModalOpen(true));
          if (waiting.game[0][2] === 'battleship') dispatch(setBattleshipModalOpen(true));
          dispatch(setOpponent(users?.find(u => u._id === (waiting.game[0][0] as string))));
        }
        dispatch(setGameStatus(waiting.game));
        dispatch(setGameResult(result[0] === 'won' ? ['lost'] : result));
      }
    };

    socket?.on('userUpdate', handleUserUpdate);
    socket?.on('questionUpdate', handleQuestionUpdate);
    socket?.on('answerUpdate', handleAnswerUpdate);
    socket?.on('commentUpdate', handleCommentUpdate);
    socket?.on('gameUpdate', handleGameUpdate);
    return () => {
      socket?.off('userUpdate', handleUserUpdate);
      socket?.on('questionUpdate', handleQuestionUpdate);
      socket?.on('answerUpdate', handleAnswerUpdate);
      socket?.on('commentUpdate', handleCommentUpdate);
      socket?.off('gameUpdate', handleGameUpdate);
    };
  }, [user, dispatch, socket, users]);

  // Add app persistance for the user's authentication status
  const authStatus = useAppSelector(authStatusSelector);

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Wait for initial load from Redux to complete
    if (authStatus !== 'loading') {
      setIsCheckingAuth(false);
    }
  }, [authStatus]);

  // Repopulates all questions and tags when a user signs in
  useEffect(() => {
    if (user?._id && !isCheckingAuth) {
      dispatch(fetchQuestions('newest'));
      dispatch(fetchTags());
    }
  }, [dispatch, user?._id, isCheckingAuth]);

  // Repopulates all users when the user's info changes or they interact with others
  useEffect(() => {
    if (user && !isCheckingAuth) {
      dispatch(fetchUsers());
    }
  }, [dispatch, user, isCheckingAuth]);

  return { user, isCheckingAuth };
};

export default useByteBuddies;
